import type { Context, Plugin } from '@segment/analytics-next'
import { getOneTrustConsent, transformOneTrustConsent } from '@susu/cookie'
import { getOptionalLocaleFromPathname } from '@susu/locale-utils'

import { debugLogEvent, isAnalyticsDebugEnabled } from './debug'
import { isBrowser } from './env'
import { getDeclaredPageType, getPageCommonProperties } from './page-context'
import { createGAdata } from './utils'

/**
 * Derives the Segment page `path` from a locale-prefixed pathname.
 * Homepage (`/en-us/`) returns the locale (`en-us`).
 * Other pages (`/en-us/cart`) return the path after the locale (`cart`).
 */
function getSegmentPath(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length <= 1) {
    return segments[0] ?? ''
  }
  return segments.slice(1).join('/')
}

/**
 * Creates a Segment enrichment plugin that adds consent, locale, page context,
 * and common properties (including marketing click IDs from @susu/segment-common).
 * SSR-safe.
 * @returns Segment Plugin instance configured for Suitsupply GA integration
 */
export const integrationPlugin = (): Plugin => {
  let currentUrl: string | undefined
  let referrerUrl: string | undefined

  const withConsentContext = (ctx: Context): Context => {
    if (!isBrowser()) {
      return ctx
    }

    const url = new URL(window.location.href)
    if (url.href !== currentUrl) {
      referrerUrl = currentUrl ?? document.referrer
      currentUrl = url.href
    }
    const referrer = referrerUrl ?? document.referrer

    ctx.updateEvent('context', {
      ...ctx.event.context,
      consent: transformOneTrustConsent(getOneTrustConsent()),
      locale: getOptionalLocaleFromPathname(url.pathname),
      page: {
        ...ctx.event.context?.page,
        path: getSegmentPath(url.pathname),
        referrer,
        search: url.search,
        url: url.href,
      },
    })

    // Page-level values, the same for every application on the page. The page
    // owner completes them where the page says nothing (a path without a
    // locale, an in-store customer); a composed application cannot.
    const common = getPageCommonProperties()
    const properties: Record<string, unknown> = {
      ...ctx.event.properties,
      ...common,
      // page_type is page-level: the page owner's declared one wins. Until an
      // owner declares it, a page_type the app set (e.g. storefront's
      // store_overview/store_details, which the path alone can't tell apart)
      // still wins over the path-based default.
      page_type:
        getDeclaredPageType() ??
        (ctx.event.properties?.page_type || common.page_type),
    }
    // `url` is archived in the tracking plan as an event property, and
    // context.page.url carries it. Page calls keep `properties.url` (the
    // warehouse's pages.url) as segment-common's locale-stripped url.
    if (ctx.event.type === 'track') {
      delete properties.url
    }
    ctx.updateEvent('properties', properties)

    if (isAnalyticsDebugEnabled()) {
      debugLogEvent(
        ctx.event.type,
        ctx.event.event ?? ctx.event.name,
        ctx.event,
      )
    }

    return ctx
  }

  return {
    name: 'Suitsupply GA Integration Plugin',
    type: 'enrichment',
    version: '1',
    isLoaded: () => true,
    identify: withConsentContext,
    load: createGAdata,
    page: withConsentContext,
    track: withConsentContext,
  }
}
